import { useMemo } from 'react';

import {
  ObservationStatusType,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
} from '~/components/maud/patients/profile/clinical-profile/clinical-measurement/clinical-measurement-modal/constants';
import {
  warningTitleMap,
  WarningTypes,
} from '~/lib/constants/patients.constants';
import { useGetPatientClinicalMeasurementsQuery } from '~/store/services/clinicalData';

export interface EquipScaleWarning {
  type: WarningTypes;
  title: string;
}

const AGE_LIMIT = 18;
const SCALE_WEIGHT_LIMIT = 400;

const VALID_STATUSES: ObservationStatusType[] = [
  ObservationStatusType.CORRECTED,
  ObservationStatusType.FINAL,
];

const useEquipScaleWarnings = (patientId: string, age: number) => {
  const { data: weightResponse } = useGetPatientClinicalMeasurementsQuery({
    patientExternalId: patientId,
    filterBy: {
      'observation-status': VALID_STATUSES,
      'observation-type':
        PatientVitalsTabsCollection[PatientVitalsTabs.WEIGHT].filterBy,
    },
  });

  const weightData = weightResponse?.data ?? [];
  const latestWeight = weightData[0]?.value;
  const hasRecentWeight = weightData.length > 0;

  const warnings = useMemo(() => {
    const result: EquipScaleWarning[] = [];
    if (!hasRecentWeight)
      result.push({
        type: WarningTypes.NO_RECENT_WEIGHT,
        title: warningTitleMap[WarningTypes.NO_RECENT_WEIGHT],
      });
    if (latestWeight >= SCALE_WEIGHT_LIMIT)
      result.push({
        type: WarningTypes.EXCEEDS_SCALE_LIMIT,
        title: warningTitleMap[WarningTypes.EXCEEDS_SCALE_LIMIT],
      });
    if (age < AGE_LIMIT)
      result.push({
        type: WarningTypes.UNDER_18,
        title: warningTitleMap[WarningTypes.UNDER_18],
      });

    return result;
  }, [age, latestWeight, hasRecentWeight]);

  return { warnings };
};

export default useEquipScaleWarnings;
